/*
 * The tag a card in the week or month overview wears to say which trade (yrkesgrupp) the work is
 * for: a short name and a colour. A trade has only a name, so both are worked out from the
 * trades themselves, the same way every time, rather than set by anyone.
 *
 * Everything here works off getTrades(companyKey, true), retired trades included: an order keeps a
 * retired trade, and its card is tagged like any other.
 */

// How many colours the tags take turns with. The palette that draws them has this many.
export const TRADE_TAG_COLOR_COUNT = 8;

// A short name is this long, or the name's whole first word where that is shorter.
const SHORT_LENGTH = 3;

const SEPARATORS = /[\s\-/_]+/;

// The letters and digits of a piece of a name, without its punctuation.
const lettersOf = (text) => Array.from(text).filter((character) => /[\p{L}\p{N}]/u.test(character)).join("");

/*
 * A trade's letters and where its short name starts. The short name is the start of the name's
 * first word, and grows from the whole name with its separators left out, so "El-laget" is "El"
 * and grows to "Ell".
 */
const toEntry = (trade) => {
  const words = (trade.name ?? "").split(SEPARATORS).map(lettersOf).filter(Boolean);
  const letters = words.join("");
  return {
    trade,
    letters,
    length: Math.min(SHORT_LENGTH, words[0]?.length ?? 0),
  };
};

const labelOf = (entry) => entry.letters.slice(0, entry.length);

/*
 * Where two trades would have the same short name, both grow by a letter, and again, until every
 * short name is its own. Letter case does not tell two names apart: "VVS" and "vvs" read as one.
 * A trade that has no more letters to give keeps what it has, so the others grow away from it. Two
 * names with the very same letters ("El-laget", "El laget") stay alike, and their colours tell
 * them apart.
 */
const growApart = (entries) => {
  for (;;) {
    const byLabel = new Map();
    for (const entry of entries) {
      const key = labelOf(entry).toLocaleLowerCase();
      byLabel.set(key, [...(byLabel.get(key) ?? []), entry]);
    }

    let grew = false;
    for (const clash of byLabel.values()) {
      if (clash.length < 2) continue;
      for (const entry of clash) {
        if (entry.length < entry.letters.length) {
          entry.length += 1;
          grew = true;
        }
      }
    }
    if (!grew) return;
  }
};

/*
 * Each trade's tag, as a Map from its id to { label, name, colorIndex }.
 *
 * label is the short name, in the name's own letter case: "Elektriker" is "Ele", "Mekaniker"
 * "Mek", "El-laget" "El", "VVS" "VVS". A name with no letters at all is tagged by its id.
 *
 * colorIndex goes by the trade's place among the company's trades in id order, so a trade keeps
 * its colour: a new trade has a higher id and comes last, and a trade is retired rather than
 * deleted, so nothing ahead of it ever goes. Past TRADE_TAG_COLOR_COUNT trades the colours come
 * round again, and the short name still tells them apart.
 */
export function getTradeTags(trades) {
  const entries = (trades ?? []).map(toEntry);
  growApart(entries.filter((entry) => entry.letters.length > 0));

  const rankById = new Map(
    [...(trades ?? [])]
      .map((trade) => trade.id)
      .sort((a, b) => a - b)
      .map((id, index) => [id, index]),
  );

  return new Map(entries.map((entry) => [
    entry.trade.id,
    {
      label: entry.letters.length > 0 ? labelOf(entry) : `#${entry.trade.id}`,
      name: entry.trade.name,
      colorIndex: rankById.get(entry.trade.id) % TRADE_TAG_COLOR_COUNT,
    },
  ]));
}
